/**
 * Privacy Policy content (IT + EN), structured as ordered sections so the
 * Privacy.jsx page can render them with consistent typography.
 *
 * Important placeholders to update with the operator's real legal data:
 *   {TITOLARE}   — legal entity name + VAT/CF
 *   {INDIRIZZO}  — full registered address
 *   {DPO}        — DPO name + email (or "Non designato")
 *   {EMAIL}      — privacy contact email
 *   {SEDE}       — city/province
 *
 * Last revised: 2026-05-14.
 */

const SUBSTITUTIONS = {
  EMAIL: "info@dealrating.it",
  SEDE: "Lecce (LE), Italia",
  TITOLARE: "Deal Rating (titolare in fase di formalizzazione)",
  INDIRIZZO: "Lecce (LE), Italia",
  DPO: "Non designato (operatore di piccole dimensioni — art. 37 GDPR non applicabile)",
};

const apply = (str) =>
  str.replace(/\{(\w+)\}/g, (_, k) => SUBSTITUTIONS[k] || `{${k}}`);

export const PRIVACY_IT = {
  title: "Informativa sulla Privacy",
  updated: "Ultima modifica: 14 maggio 2026",
  intro:
    "La presente Informativa descrive le modalità con cui Deal Rating (di seguito anche il “Sito” o il “Servizio”) raccoglie e tratta i dati personali degli utenti, in conformità al Regolamento (UE) 2016/679 (GDPR) e al D.lgs. 196/2003 (Codice Privacy) come modificato dal D.lgs. 101/2018.",
  sections: [
    {
      h: "1. Titolare del trattamento",
      p: apply(
        "Titolare del trattamento dei dati è {TITOLARE}, con sede in {INDIRIZZO}. Per qualsiasi richiesta relativa al trattamento dei dati personali è possibile scrivere a: {EMAIL}.",
      ),
    },
    {
      h: "2. Responsabile della protezione dei dati (DPO)",
      p: apply("{DPO}"),
    },
    {
      h: "3. Categorie di dati trattati",
      p: "Il Sito tratta le seguenti categorie di dati personali:",
      list: [
        "Dati identificativi e di contatto: nome, cognome, indirizzo email e immagine del profilo Google, raccolti tramite il flusso di accesso “Continua con Google”.",
        "Dati di navigazione: indirizzo IP, hash dell’IP per limitare le richieste anonime, user-agent, pagine visitate, timestamp delle richieste.",
        "Dati relativi al servizio: URL e/o testo degli annunci immobiliari inseriti dall’utente per essere analizzati, risultati delle analisi generate dall’intelligenza artificiale (punteggio, analisi economica, rischi, stime), eventuali liste di immobili salvate (shortlist).",
        "Dati di pagamento: gestiti integralmente da Stripe Payments Europe Limited; il Sito non memorizza dati di carta di credito ma solo l’identificativo della transazione, l’importo, la valuta, lo stato del pagamento e il customer ID Stripe per associare l’acquisto all’account.",
        "Dati di telemetria del paywall e delle quote di utilizzo: numero di report generati, residuo crediti, scadenza dell’eventuale abbonamento Premium.",
        "Cookie tecnici e, previo consenso, cookie analitici di terze parti.",
      ],
    },
    {
      h: "4. Finalità e base giuridica",
      p: "Trattiamo i Suoi dati per le seguenti finalità:",
      list: [
        "Erogazione del servizio (analisi annunci, generazione report, gestione dell’account, shortlist e archivio): base giuridica art. 6, par. 1, lett. b) GDPR — esecuzione di un contratto di cui l’interessato è parte.",
        "Gestione dei pagamenti e adempimenti fiscali: base giuridica art. 6, par. 1, lett. b) e c) GDPR — esecuzione del contratto e obbligo legale.",
        "Sicurezza, prevenzione di abusi, rate-limiting basato su hash di IP, contrasto a uso fraudolento: base giuridica art. 6, par. 1, lett. f) GDPR — legittimo interesse del titolare a proteggere il Servizio.",
        "Statistica anonima e miglioramento del prodotto tramite Google Analytics 4 e PostHog: base giuridica art. 6, par. 1, lett. a) GDPR — consenso, prestato tramite il banner cookie.",
        "Comunicazioni di servizio (es. conferme di pagamento, notifiche di scadenza abbonamento): base giuridica art. 6, par. 1, lett. b) GDPR.",
      ],
    },
    {
      h: "5. Modalità del trattamento",
      p: "Il trattamento avviene con strumenti elettronici, ospitati su infrastruttura cloud Emergent (Stati Uniti) e MongoDB. Sono adottate misure tecniche e organizzative adeguate (cifratura in transito HTTPS/TLS, controllo accessi, principio di minimizzazione, sessione utente firmata HttpOnly).",
    },
    {
      h: "6. Destinatari e responsabili esterni",
      p: "I Suoi dati possono essere comunicati ai seguenti soggetti, designati responsabili del trattamento ai sensi dell’art. 28 GDPR o titolari autonomi nei rispettivi servizi:",
      list: [
        "Google LLC / Google Ireland Limited — autenticazione (OAuth), Google Analytics 4 (statistiche aggregate).",
        "Anthropic PBC — fornitore del modello linguistico Claude utilizzato per generare le analisi (riceve solo il testo dell’annuncio, non i dati identificativi dell’utente).",
        "Stripe Payments Europe Limited — gestione dei pagamenti.",
        "Firecrawl Inc. — estrazione automatizzata del contenuto dell’annuncio dall’URL fornito.",
        "Emergent Inc. — fornitore dell’infrastruttura di hosting e dell’integrazione di autenticazione.",
        "PostHog, Inc. — analitica di prodotto e session replay (attivati solo dopo consenso esplicito).",
        "Eventuali autorità pubbliche e organi giudiziari, ove richiesto dalla legge.",
      ],
    },
    {
      h: "7. Trasferimento dei dati all’estero",
      p: "Alcuni dei fornitori sopra elencati hanno sede negli Stati Uniti (Anthropic, Stripe statunitense, Firecrawl, Emergent, PostHog, Google). Il trasferimento avviene sulla base di Clausole Contrattuali Standard adottate dalla Commissione Europea (Decisione 2021/914) e, ove applicabile, ai sensi dell’EU–US Data Privacy Framework.",
    },
    {
      h: "8. Periodo di conservazione",
      list: [
        "Dati dell’account utente: per tutta la durata del rapporto contrattuale e fino a 24 mesi dall’ultimo accesso, salvo richiesta di cancellazione anticipata.",
        "Analisi degli annunci e shortlist: associate all’account fino a sua cancellazione; per gli utenti guest, conservate per un massimo di 12 mesi.",
        "Dati di pagamento e fatturazione: 10 anni come previsto dalla normativa fiscale italiana (art. 2220 c.c.).",
        "Dati di telemetria scraping e hash IP: 30 giorni (rolling TTL).",
        "Cookie analitici: massimo 13 mesi (Linee Guida del Garante).",
      ],
    },
    {
      h: "9. Diritti dell’interessato",
      p: "Ai sensi degli artt. 15-22 GDPR, l’utente ha diritto in qualunque momento di:",
      list: [
        "Accedere ai propri dati personali e ottenerne copia;",
        "Richiedere la rettifica o l’aggiornamento dei dati inesatti;",
        "Richiedere la cancellazione dei dati (“diritto all’oblio”);",
        "Richiedere la limitazione del trattamento;",
        "Opporsi al trattamento basato su legittimo interesse;",
        "Esercitare il diritto alla portabilità dei dati in formato leggibile;",
        "Revocare in qualunque momento il consenso prestato per le finalità statistiche (gestione cookie disponibile dal banner / Privacy Policy);",
        "Proporre reclamo al Garante per la Protezione dei Dati Personali (www.garanteprivacy.it).",
      ],
      footer: apply(
        "Per esercitare i propri diritti è sufficiente scrivere a {EMAIL}. La risposta avverrà entro 30 giorni dalla ricezione della richiesta.",
      ),
    },
    {
      h: "10. Cookie",
      p: "Il Sito utilizza le seguenti categorie di cookie:",
      list: [
        "Cookie tecnici di sessione: gestiscono la navigazione dell’utente e mantengono la sessione attiva tra una pagina e l’altra.",
        "Cookie di autenticazione: permettono di mantenere il login attivo dopo l’accesso tramite Google, evitando di reinserire le credenziali ad ogni pagina.",
        "Cookie di bilanciamento del carico: distribuiscono il traffico tra i server per mantenere il sito veloce e funzionante.",
        "Cookie di personalizzazione limitati: memorizzano la lingua selezionata (it/en) e lo stato del consenso ai cookie analitici.",
        "Cookie analitici di terze parti (Google Analytics 4 con IP anonimizzato; PostHog): attivati esclusivamente previo consenso espresso tramite il banner cookie.",
      ],
      footer: "Per i cookie tecnici, di autenticazione, di bilanciamento del carico e di personalizzazione limitata NON è richiesto il consenso ai sensi del Provvedimento del Garante n. 231/2021: sono strettamente necessari all’erogazione del Servizio richiesto dall’utente. Per i cookie analitici di terze parti è sempre possibile modificare la propria scelta dalla sezione “Gestisci il tuo consenso” in fondo a questa pagina.",
    },
    {
      h: "11. Profilazione e decisioni automatizzate",
      p: "Il punteggio “Deal Score” fornito sui report è generato in modo automatizzato da un modello di intelligenza artificiale a partire dai dati pubblici dell’annuncio. Tale valutazione ha finalità puramente informativa, non costituisce consulenza professionale e non produce effetti giuridici sull’utente: non è quindi un trattamento di profilazione ex art. 22 GDPR. In ogni caso, l’utente può richiedere chiarimenti sulla logica del punteggio scrivendo al Titolare.",
    },
    {
      h: "12. Modifiche all’informativa",
      p: "La presente Informativa può essere aggiornata in qualsiasi momento per adeguarla a modifiche normative o operative. La versione vigente è sempre disponibile a questa URL; consigliamo agli utenti di consultarla periodicamente. Le modifiche sostanziali saranno notificate con evidenza nella home page del Sito.",
    },
  ],
};

export const PRIVACY_EN = {
  title: "Privacy Policy",
  updated: "Last revision: 14 May 2026",
  intro:
    "This Privacy Policy describes how Deal Rating (the “Site” or the “Service”) collects and processes the personal data of its users, pursuant to Regulation (EU) 2016/679 (GDPR) and Italian Legislative Decree 196/2003 as amended.",
  sections: [
    {
      h: "1. Data Controller",
      p: apply(
        "The data controller is {TITOLARE}, with registered office at {INDIRIZZO}. For any privacy request, please contact: {EMAIL}.",
      ),
    },
    {
      h: "2. Data Protection Officer (DPO)",
      p: apply("{DPO}"),
    },
    {
      h: "3. Categories of data processed",
      p: "The Site processes the following categories of personal data:",
      list: [
        "Identification and contact data: name, surname, email and profile picture provided by Google through the “Continue with Google” sign-in flow.",
        "Navigation data: IP address, hashed IP for anonymous rate-limiting, user-agent, pages visited, request timestamps.",
        "Service data: URLs and/or text of real-estate listings submitted by the user for analysis, AI-generated reports (score, economic analysis, risks, estimates), saved shortlists.",
        "Payment data: handled entirely by Stripe Payments Europe Limited. The Site stores only the transaction ID, amount, currency, status and Stripe customer ID — never raw card data.",
        "Paywall and quota telemetry: number of reports generated, remaining credits, Premium subscription expiry.",
        "Technical cookies and, subject to consent, third-party analytics cookies.",
      ],
    },
    {
      h: "4. Purposes and legal basis",
      p: "We process your data for the following purposes:",
      list: [
        "Service provision (listing analysis, report generation, account management, shortlist): art. 6(1)(b) GDPR — performance of a contract.",
        "Payment management and tax compliance: art. 6(1)(b) and (c) GDPR.",
        "Security, abuse prevention, IP-hash rate-limiting: art. 6(1)(f) GDPR — legitimate interest in protecting the Service.",
        "Anonymous statistics and product improvement via Google Analytics 4 and PostHog: art. 6(1)(a) GDPR — consent, granted through the cookie banner.",
        "Service communications (e.g., payment confirmations, subscription expiry notices): art. 6(1)(b) GDPR.",
      ],
    },
    {
      h: "5. Means of processing",
      p: "Processing is performed with electronic tools hosted on Emergent cloud infrastructure (United States) and MongoDB. Adequate technical and organisational measures are in place (HTTPS/TLS encryption in transit, access controls, data minimisation, HttpOnly signed session cookies).",
    },
    {
      h: "6. Recipients and processors",
      p: "Your data may be shared with the following parties, designated as processors under art. 28 GDPR or acting as autonomous controllers for their respective services:",
      list: [
        "Google LLC / Google Ireland Limited — authentication (OAuth), Google Analytics 4.",
        "Anthropic PBC — provider of the Claude language model used to generate reports (receives only the listing text, no user identifiers).",
        "Stripe Payments Europe Limited — payment processing.",
        "Firecrawl Inc. — automated extraction of listing content from submitted URLs.",
        "Emergent Inc. — hosting and authentication infrastructure provider.",
        "PostHog, Inc. — product analytics and session replay (enabled only after explicit consent).",
        "Public authorities and courts, where required by law.",
      ],
    },
    {
      h: "7. International transfers",
      p: "Some processors above are based in the United States (Anthropic, Stripe US, Firecrawl, Emergent, PostHog, Google). Transfers occur under Standard Contractual Clauses adopted by the European Commission (Decision 2021/914) and, where applicable, under the EU-US Data Privacy Framework.",
    },
    {
      h: "8. Retention period",
      list: [
        "User account data: for the duration of the relationship and up to 24 months from the last login, unless deletion is requested earlier.",
        "Listing analyses and shortlists: tied to the account until deletion; for guest users, max 12 months.",
        "Payment and invoicing data: 10 years as required by Italian tax law (art. 2220 c.c.).",
        "Scraping telemetry and IP hashes: 30 days (rolling TTL).",
        "Analytics cookies: max 13 months (Italian DPA guidelines).",
      ],
    },
    {
      h: "9. Your rights",
      p: "Under articles 15-22 GDPR you have the right, at any time, to:",
      list: [
        "Access your personal data and obtain a copy;",
        "Request rectification or update of inaccurate data;",
        "Request erasure (“right to be forgotten”);",
        "Request restriction of processing;",
        "Object to processing based on legitimate interest;",
        "Exercise the right to data portability;",
        "Withdraw consent given for analytics at any time (manage from the banner or Privacy page);",
        "Lodge a complaint with the Italian Data Protection Authority (www.garanteprivacy.it).",
      ],
      footer: apply(
        "To exercise your rights, please write to {EMAIL}. We will respond within 30 days.",
      ),
    },
    {
      h: "10. Cookies",
      p: "The Site uses the following cookie categories:",
      list: [
        "Technical session cookies: handle user navigation and keep the session active between pages.",
        "Authentication cookies: keep the user logged-in after sign-in with Google, avoiding repeated credential entry.",
        "Load-balancing cookies: distribute traffic across servers to keep the site fast and reliable.",
        "Limited personalisation cookies: store the selected language (it/en) and the analytics consent state.",
        "Third-party analytics cookies (Google Analytics 4 with IP anonymisation; PostHog): enabled only after explicit consent via the cookie banner.",
      ],
      footer: "Technical, authentication, load-balancing and limited-personalisation cookies do NOT require consent under Italian DPA decision no. 231/2021: they are strictly necessary to provide the Service requested by the user. For third-party analytics cookies you can change your choice any time from the “Manage your consent” section at the bottom of this page.",
    },
    {
      h: "11. Profiling and automated decisions",
      p: "The “Deal Score” shown in reports is generated automatically by an AI model from public listing data. It is informational only, does not constitute professional advice, and produces no legal effects on the user — so it is not profiling under art. 22 GDPR. You may request clarification on the scoring logic at any time by contacting the controller.",
    },
    {
      h: "12. Changes to this policy",
      p: "This Policy may be updated to reflect legal or operational changes. The current version is always available at this URL; users are encouraged to consult it periodically. Material changes will be prominently notified on the Site’s home page.",
    },
  ],
};
